import type { VideoSubtitleCacheEntry } from "@lib/videoSubtitle";
import type { MobileVideo } from "./mobileVideoStore";

type SubtitleItem = Pick<MobileVideo, "download" | "fileUploadedAt"> & {
  srtVersion?: string | null;
  subtitleCache?: VideoSubtitleCacheEntry | null;
  subtitleCacheError?: string | null;
};
export type OfflineSubtitleState = {
  status:
    | "unknown"
    | "ready"
    | "absent"
    | "update-pending"
    | "failed"
    | "file-mismatch";
  cached: VideoSubtitleCacheEntry | null;
};

export function describeOfflineSubtitles(state: OfflineSubtitleState): string {
  switch (state.status) {
    case "ready":
      return "Subtitles ready offline";
    case "absent":
      return "No subtitles are attached to this video.";
    case "update-pending":
      return "Saved subtitles are available; an update is waiting to download.";
    case "file-mismatch":
      return "Update the saved video to download matching subtitles.";
    case "failed":
      return "Subtitles could not be downloaded. Connect and retry in Downloads.";
    case "unknown":
      return "Subtitles haven't been downloaded yet. Connect to download them.";
  }
}

export function summarizeOfflineSubtitles(videos: readonly MobileVideo[]): {
  ready: number;
  absent: number;
  pending: number;
} {
  const counts = { ready: 0, absent: 0, pending: 0 };

  for (const video of videos) {
    if (video.removedFromCatalog || video.download?.status !== "completed") {
      continue;
    }

    const { status } = getOfflineSubtitleState(video);

    if (status === "ready") {
      counts.ready++;
    } else if (status === "absent") {
      counts.absent++;
    } else {
      counts.pending++;
    }
  }

  return counts;
}

export function getOfflineSubtitleState(
  item: SubtitleItem,
): OfflineSubtitleState {
  const downloadedFile = item.download?.fileUploadedAt;

  // null is a known legacy movie generation; only undefined is unstamped.
  if (downloadedFile === undefined) {
    return { status: "file-mismatch", cached: null };
  }

  const cached =
    item.subtitleCache?.fileUploadedAt === downloadedFile
      ? item.subtitleCache
      : null;

  if (item.fileUploadedAt !== downloadedFile) {
    return { status: "file-mismatch", cached };
  }

  if (item.srtVersion === null) {
    return { status: "absent", cached: null };
  }

  if (cached?.content != null) {
    return {
      status:
        item.srtVersion === undefined || item.srtVersion === cached.srtVersion
          ? "ready"
          : "update-pending",
      cached,
    };
  }

  if (cached?.srtVersion === null && item.srtVersion === undefined) {
    return { status: "absent", cached: null };
  }

  return {
    status: item.subtitleCacheError ? "failed" : "unknown",
    cached: null,
  };
}
